import { useMemo } from 'react';
import { useOrganizationId, useSubjectLanguageVariants } from '@features/user';

import type { MissingRequirementRowView } from '../components/MissingRequirementListView';
import type { RequirementKind } from '../constants';
import { outcomeDetail, requestedDetail, resolveDisplayState } from '../vocabulary';

import { useGetMissingRequirementsQuery } from './api';
import { useRequirementCatalog } from './hooks';

const DEFAULT_PER_PAGE = 25;

export interface MissingRequirementsResult {
  rows: MissingRequirementRowView[];
  total: number;
  page: number;
  perPage: number;
  isLoading: boolean;
  isError: boolean;
  /** The definition being listed, once the catalog resolves it. */
  requirementLabel: string;
  /** True when the org has no definition of this kind at all. */
  definitionMissing: boolean;
  refetch: () => void;
}

/**
 * The missing list for one requirement kind (AC-2).
 *
 * The endpoint requires a definition id, so this resolves the org's definition
 * of the requested kind from the catalog first and stays in a loading state
 * until it has one — querying with a blank id would 404 rather than return an
 * empty list, which reads as an error instead of "nothing to show".
 */
export const useMissingRequirements = (
  kind: RequirementKind,
  page: number,
  perPage: number = DEFAULT_PER_PAGE,
  /**
   * False when the caller will not render — a gate-off org, or a user without
   * the permission. Hooks cannot be called conditionally, so the caller's early
   * return happens AFTER this runs; without threading the flag down we would
   * fire an org catalog read and a worklist read for a surface nobody sees.
   */
  enabled: boolean = true,
): MissingRequirementsResult => {
  const organizationId = useOrganizationId();
  const subject = useSubjectLanguageVariants();
  const catalog = useRequirementCatalog({ skip: !enabled });

  const definition = useMemo(
    () => catalog.definitions.find(candidate => candidate.kind === kind),
    [catalog.definitions, kind],
  );

  const definitionId = definition?.patient_requirement_definition_id;
  const skip = !enabled || !organizationId || !definitionId;

  const { data, isLoading, isFetching, isError, refetch } = useGetMissingRequirementsQuery(
    {
      orgId: organizationId ?? '',
      patientRequirementDefinitionId: definitionId ?? '',
      page,
      perPage,
    },
    { skip },
  );

  const rows = useMemo<MissingRequirementRowView[]>(
    () =>
      (data?.items ?? []).map(item => {
        // Both former gaps are closed by the synced contract: the row now
        // carries patient identity, and `has_active_session` means an open row
        // that has been chased reads Requested instead of Missing.
        const hasActiveSession = item.has_active_session ?? false;
        const state = resolveDisplayState(item, hasActiveSession);

        let detail: string | undefined;
        if (state === 'requested') {
          // Shared with the record section so one requirement cannot read two
          // different ways depending on which screen staff opened.
          detail = requestedDetail(item.last_requested_at);
        } else {
        const outcome = item.last_request_outcome;
          // Gated on `missing` rather than taken from the else, even though
          // this endpoint returns outstanding rows only. Relying on that would
          // make the correctness of this line a property of the ENDPOINT
          // rather than of the code, and the day it starts returning settled
          // rows nothing here would notice — the record section had exactly
          // that bug, from exactly this shape.
          //
          // Ahead of "Assigned <date>" on purpose: a requirement that was
          // chased and bounced is a different row from one merely assigned,
          // and the assignment date is the less useful of the two facts.
          if (state === 'missing') {
            detail = outcomeDetail(outcome, subject);
          }
          if (!detail && item.assigned_at) {
            detail = `Assigned ${new Date(item.assigned_at).toLocaleDateString()}`;
          }
        }

        return {
          patientRequirementId: item.patient_requirement_id,
          patientId: item.patient_id,
          // Documented nullable ("None if unloadable"), so the view's
          // shortened-id fallback stays — a blank name cell would read as a
          // patient with no name rather than a row we could not resolve.
          patientName: item.patient_display_name ?? undefined,
          // Disambiguates same-named patients on a chase list. Staff surface,
          // so this is fine on screen; it never reaches an analytics payload.
          patientDob: item.patient_dob ?? undefined,
          state,
          detail,
        };
      }),
    [data, subject],
  );

  return {
    rows,
    total: data?.total ?? 0,
    page: data?.page ?? page,
    perPage: data?.per_page ?? perPage,
    isLoading: enabled && (catalog.isLoading || (!skip && (isLoading || isFetching))),
    isError: catalog.isError || isError,
    requirementLabel: definition?.label ?? '',
    // Only meaningful once the catalog has actually loaded.
    definitionMissing:
      enabled && !catalog.isLoading && !catalog.isError && definition === undefined,
    // Retry has to refetch the catalog too. When the catalog is what failed
    // there is no definition id, so the missing-list query is SKIPPED — and
    // refetching a skipped query does nothing, leaving the page stuck on its
    // error with a button that appears to work.
    refetch: () => {
      catalog.refetch();
      if (!skip) void refetch();
    },
  };
};
